import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import type { DeviceConnection } from "../../../api/deviceConnection";
import { AllDataDevice } from "../../../generated/types";
import { DataTableColumn, DataTableRow } from "../../common/DataTable";
import { FavoriteButton, FavoriteHeader } from "../../common/FavoriteButton";
import { ConnectionDot, isRemoteBlocked } from "./ConnectionDot";
import { DeviceTypeBadge } from "./DeviceTypeBadge";

export type DeviceColumnKey =
  | "favorite"
  | "connection"
  | "type"
  | "name"
  | "uuid"
  | "company"
  | "breeding"
  | "building"
  | "actions";

/** Les colonnes triables sont celles qui ont une valeur de tri. */
export const DEVICE_SORT_VALUES: Partial<
  Record<DeviceColumnKey, (device: AllDataDevice) => string>
> = {
  type: (d) => d.device?.type ?? "",
  name: (d) => d.device?.name ?? "",
  uuid: (d) => d.device?.uuid ?? "",
  company: (d) => d.company?.company_name ?? "",
  breeding: (d) => d.breeding?.name ?? "",
  building: (d) => d.building?.name ?? "",
};

/**
 * `showUuid` : colonne réservée aux espaces NON-admin. L'écran admin a déjà ses
 * 7 colonnes remplies, en ajouter une huitième les tasserait — alors que pour un
 * éleveur ou un installateur, élevage et bâtiment sont vides et l'UUID est
 * l'identifiant qu'ils manipulent (association, support, mot de passe produit).
 */
export const useDeviceColumns = (
  showUuid: boolean
): DataTableColumn<DeviceColumnKey>[] => {
  const { t } = useTranslation();
  const columns: DataTableColumn<DeviceColumnKey>[] = [
    {
      key: "favorite",
      header: <FavoriteHeader />,
      className: "w-8 flex items-center justify-center",
    },
    {
      key: "connection",
      header: t("device-connection"),
      className: "w-1/12 flex justify-center",
    },
    { key: "type", header: t("device-type"), className: "w-2/12" },
    { key: "name", header: t("device-name"), className: "w-2/12" },
    ...(showUuid
      ? [
          {
            key: "uuid" as const,
            header: t("device-uuid"),
            className: "w-2/12 truncate",
          },
        ]
      : []),
    { key: "company", header: t("section-company"), className: "w-2/12" },
    { key: "breeding", header: t("section-breeding"), className: "w-2/12" },
    {
      key: "building",
      header: t("section-building"),
      className: "hidden lg:block w-2/12",
    },
    { key: "actions", header: "", className: "w-1/12 text-right" },
  ];
  return columns.map((column) => ({
    ...column,
    sortable: column.key in DEVICE_SORT_VALUES,
  }));
};

type DeviceRowProps = {
  device: AllDataDevice;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  onClick: () => void;
  onViewDevice?: (e: React.MouseEvent) => void;
  showUuid?: boolean;
  /** État de connexion VPN de la box : pastille, et œil grisé si elle est déconnectée. */
  connection?: DeviceConnection;
  selected?: boolean;
};

export const DeviceRow = ({
  device,
  isFavorite,
  onToggleFavorite,
  onClick,
  onViewDevice,
  showUuid = false,
  connection,
  selected = false,
}: DeviceRowProps) => {
  const { t } = useTranslation();
  const columns = useDeviceColumns(showUuid);
  if (!device.device) return null;
  const remoteBlocked = isRemoteBlocked(connection);

  return (
    <DataTableRow
      columns={columns}
      onClick={onClick}
      selected={selected}
      cells={{
        favorite: (
          <FavoriteButton isFavorite={isFavorite} onToggle={onToggleFavorite} />
        ),
        connection: <ConnectionDot connection={connection} />,
        type: device.device.type ? (
          <DeviceTypeBadge type={device.device.type} />
        ) : (
          "-"
        ),
        name: device.device.name || "-",
        uuid: (
          <span className="font-mono text-sm" title={device.device.uuid}>
            {device.device.uuid || "-"}
          </span>
        ),
        company: device.company?.company_name || "-",
        breeding: device.breeding?.name || "-",
        building: device.building?.name || "-",
        actions: onViewDevice && (
          <button
            onClick={onViewDevice}
            disabled={remoteBlocked}
            title={
              remoteBlocked
                ? t("remote-view-blocked-disconnected")
                : t("remote-view-open")
            }
            aria-label={
              remoteBlocked
                ? t("remote-view-blocked-disconnected")
                : t("remote-view-open")
            }
            className={
              remoteBlocked
                ? "text-grey-medium cursor-not-allowed"
                : "text-tertiary hover:text-secondary transition-colors"
            }
          >
            <FontAwesomeIcon icon="eye" />
          </button>
        ),
      }}
    />
  );
};
